import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Settings, 
  Globe, 
  Shield, 
  Bell, 
  Database, 
  Server,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  BarChart3
} from 'lucide-react';
import api from '../config/api';

interface AdminSettingsProps {
  onSettingChange?: (key: string, value: any) => void;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({ onSettingChange }) => {
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    comingSoonMode: false,
    maintenanceMode: false,
    debugMode: false,
    emailNotifications: true,
    analyticsEnabled: true
  });

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings/public');
      console.log('Loaded settings:', response.data);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleToggle = async (key: string) => {
    const newValue = !settings[key as keyof typeof settings];
    console.log(`Toggling ${key} to ${newValue}`);
    
    // Update local state
    setSettings(prev => ({ ...prev, [key]: newValue }));
    
    // Call parent callback if exists
    if (onSettingChange) {
      onSettingChange(key, newValue);
    }
    
    // Save to backend
    setSaving(true);
    try {
      const response = await api.post('/settings', {
        [key]: newValue
      });
      
      if (response.data.success) {
        console.log('Settings saved successfully');
        
        // Show success message for Coming Soon
        if (key === 'comingSoonMode') {
          alert(newValue 
            ? 'Coming Soon režimas ĮJUNGTAS! Visi lankytojai matys Coming Soon puslapį.\n\nBypass URL: ?bypass=admin2024' 
            : 'Coming Soon režimas IŠJUNGTAS! Svetainė vėl prieinama visiems.'
          );
        }
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Nepavyko išsaugoti nustatymų. Bandykite dar kartą.');
      // Revert on error
      setSettings(prev => ({ ...prev, [key]: !newValue }));
    } finally {
      setSaving(false);
    }
  };

  const clearBypass = () => {
    localStorage.removeItem('bypassComingSoon');
    alert('Bypass išvalytas! Perkraukite puslapį.');
    window.location.reload();
  };

  return (
    <div className="space-y-6">
      {/* Site Mode Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Site Mode Settings
          </CardTitle>
          <CardDescription>
            Control the overall state and behavior of the website
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Coming Soon Mode */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">Coming Soon Mode</h4>
                {settings.comingSoonMode && (
                  <Badge variant="destructive" className="text-xs">
                    ACTIVE
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600">
                Enable this to show Coming Soon page to all visitors. Only admins can access the full site.
              </p>
              {settings.comingSoonMode && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Warning: All users will see the coming soon page
                </p>
              )}
            </div>
            <Switch
              checked={settings.comingSoonMode}
              onChange={() => handleToggle('comingSoonMode')}
              disabled={saving}
            />
          </div>

          {/* Maintenance Mode */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">Maintenance Mode</h4>
                {settings.maintenanceMode && (
                  <Badge variant="secondary" className="text-xs">
                    ACTIVE
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600">
                Enable maintenance mode to temporarily disable the site for updates
              </p>
            </div>
            <Switch
              checked={settings.maintenanceMode}
              onChange={() => handleToggle('maintenanceMode')}
              disabled={saving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Bypass Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Bypass Management
          </CardTitle>
          <CardDescription>
            Manage Coming Soon bypass for testing and authorized access
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 border rounded-lg bg-blue-50">
            <h4 className="font-semibold mb-2">🔓 Bypass Methods</h4>
            <div className="space-y-2 text-sm">
              <p><strong>URL Parameter:</strong> <code>?bypass=admin2024</code></p>
              <p><strong>Preview Mode:</strong> <code>?preview=true</code></p>
              <p><strong>Persistent:</strong> Saved to localStorage after first use</p>
            </div>
          </div>
          
          <div className="p-4 border rounded-lg bg-yellow-50">
            <h4 className="font-semibold mb-2">⚠️ Current Bypass Status</h4>
            <p className="text-sm text-gray-600 mb-3">
              {localStorage.getItem('bypassComingSoon') === 'true' 
                ? '✅ Bypass is ACTIVE - You can access the site even with Coming Soon enabled'
                : '❌ No bypass active - Coming Soon mode will show if enabled'
              }
            </p>
            <Button 
              onClick={clearBypass}
              variant="outline"
              size="sm"
              className="text-xs"
            >
              Clear Bypass & Reload
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            System Settings
          </CardTitle>
          <CardDescription>
            Configure system-level settings and features
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Debug Mode */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">Debug Mode</h4>
                {settings.debugMode && (
                  <Badge variant="outline" className="text-xs">
                    DEBUG
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600">
                Enable debug mode for development and troubleshooting
              </p>
            </div>
            <Switch
              checked={settings.debugMode}
              onChange={() => handleToggle('debugMode')}
              disabled={saving}
            />
          </div>

          {/* Email Notifications */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">Email Notifications</h4>
                <Bell className="w-4 h-4 text-gray-500" />
              </div>
              <p className="text-sm text-gray-600">
                Enable system email notifications for users and admins
              </p>
            </div>
            <Switch
              checked={settings.emailNotifications}
              onChange={() => handleToggle('emailNotifications')}
              disabled={saving}
            />
          </div>

          {/* Analytics */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">Analytics</h4>
                <BarChart3 className="w-4 h-4 text-gray-500" />
              </div>
              <p className="text-sm text-gray-600">
                Enable analytics and usage tracking
              </p>
            </div>
            <Switch
              checked={settings.analyticsEnabled}
              onChange={() => handleToggle('analyticsEnabled')}
              disabled={saving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security Settings
          </CardTitle>
          <CardDescription>
            Configure security and access control settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Password Policy</h4>
              <p className="text-sm text-gray-600 mb-3">
                Minimum 8 characters, mixed case, numbers required
              </p>
              <Badge variant="outline" className="text-xs">
                Strong
              </Badge>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Session Timeout</h4>
              <p className="text-sm text-gray-600 mb-3">
                Auto-logout after 24 hours of inactivity
              </p>
              <Badge variant="outline" className="text-xs">
                24 hours
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="w-5 h-5" />
            System Status
          </CardTitle>
          <CardDescription>
            Current system health and performance metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 p-3 border rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium">Database</p>
                <p className="text-sm text-gray-600">Healthy</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-3 border rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium">API Server</p>
                <p className="text-sm text-gray-600">Online</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-3 border rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium">Redis Cache</p>
                <p className="text-sm text-gray-600">Connected</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading Overlay */}
      {saving && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="text-sm text-gray-600 mt-2">Updating settings...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings; 